import { Link } from 'react-router';
import { ArticleLayout, H2, H3, P, UL, LI, Callout } from './ArticleLayout';

export function StopChatgptDataLeaks() {
  return (
    <ArticleLayout
      currentPath="/stop-chatgpt-data-leaks"
      breadcrumb="Stop ChatGPT data leaks"
      eyebrow="Playbook"
      title={
        <>
          How to stop data leaks to <span className="text-red-accent">ChatGPT</span>
        </>
      }
      lead="ChatGPT and other AI chatbots have become a routine destination for sensitive data. This is a practical playbook for preventing PII, card numbers, source code and secrets from leaving your organisation through a prompt — without banning AI and losing the productivity."
    >
      <H2 id="the-problem">Why ChatGPT is a data-leak channel</H2>
      <P>
        A prompt is an outbound data transfer. When someone pastes a customer list, a contract or a
        block of code into ChatGPT, that content leaves your environment over ordinary HTTPS to a
        trusted domain — invisible to email filters, network DLP and firewalls. Depending on the
        provider and plan, it may be retained or used to improve the model. Unlike a misdirected email,
        you cannot unsend it.
      </P>

      <H2 id="what-doesnt-work">What doesn&apos;t work</H2>
      <UL>
        <LI>
          <strong className="text-text-primary">Blocking the domain.</strong> It pushes staff to
          personal devices and other AI tools, making the leak invisible instead of stopping it.
        </LI>
        <LI>
          <strong className="text-text-primary">Policy documents alone.</strong> An acceptable-use
          policy nobody enforces does not change behaviour under deadline pressure.
        </LI>
        <LI>
          <strong className="text-text-primary">Browser extensions.</strong> They only cover the
          browser — not desktop apps, IDE assistants, or the next AI tool your team adopts.
        </LI>
      </UL>

      <H2 id="the-playbook">The playbook</H2>

      <H3>1. Get visibility (log-only for two weeks)</H3>
      <P>
        Deploy endpoint detection in log-only mode first. Do not block anything yet — just measure which
        AI tools are used and what categories of data flow to them. This almost always surfaces more
        usage than expected and gives you the evidence to set proportionate policy. This is the core of{' '}
        <Link to="/ai-dlp" className="text-red-accent underline underline-offset-4 hover:text-red-hover">
          AI data loss prevention
        </Link>
        .
      </P>

      <H3>2. Classify what actually matters</H3>
      <P>
        Not all data is equal. Define the categories you must protect — PII, PCI/payment data,
        credentials and secrets, and source code — and treat everything else as low risk so you are not
        drowning in false positives.
      </P>

      <H3>3. Set graduated block / warn / log policy</H3>
      <UL>
        <LI>
          <strong className="text-text-primary">Block</strong> the highest-risk categories: secrets,
          source code, and bulk PII or payment data.
        </LI>
        <LI>
          <strong className="text-text-primary">Warn</strong> on medium-risk content, so the employee
          gets an in-the-moment prompt and can decide — this both prevents leaks and educates.
        </LI>
        <LI>
          <strong className="text-text-primary">Log</strong> everything for a full audit trail, storing
          metadata only rather than the raw content.
        </LI>
      </UL>

      <H3>4. Cover desktop apps and IDEs, not just the browser</H3>
      <P>
        ChatGPT is used in the browser, in a desktop app, and through API-backed tools and IDE
        assistants. Inspecting content at the operating-system level covers the browser, desktop apps
        and IDEs alike, across major AI services like ChatGPT, Copilot and Claude — with no per-app
        configuration.
      </P>

      <H3>5. Keep detection on the device</H3>
      <P>
        If your DLP ships prompt content to the cloud to be scanned, you have added a second data-egress
        path. Detection that runs locally classifies the content on the endpoint and never transmits it,
        which is both safer and faster.
      </P>

      <Callout title="How Redbax does it">
        <P>
          Redbax runs on Windows and Linux and inspects prompts and uploads at the OS level — before
          they reach ChatGPT or any of 40+ AI services. Detection is local, latency is under five
          milliseconds, and the SOC dashboard stores metadata only. See{' '}
          <Link to="/features" className="text-red-accent underline underline-offset-4 hover:text-red-hover">
            how it works
          </Link>
          .
        </P>
      </Callout>

      <P>
        Related: understand the behaviour behind the risk in our{' '}
        <Link to="/shadow-ai" className="text-red-accent underline underline-offset-4 hover:text-red-hover">
          shadow AI guide
        </Link>
        , or{' '}
        <Link to="/contact" className="text-red-accent underline underline-offset-4 hover:text-red-hover">
          register your interest
        </Link>{' '}
        to see Redbax in action.
      </P>
    </ArticleLayout>
  );
}
